import { I18n } from "@assets/strings";
import { isArray } from "lodash";
import { useObservable } from "micro-observables";
import React from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import styled from "styled-components";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { stack } from "@ui/components/layout";
import { gap, mobile } from "@ui/styles/utils";
import { AddSectionMenu } from "./addSectionMenu";
import { useEditorController } from "@ui/pages/lms/article/articleEditorPage/articleEditorController/editorControllerContext";
import { ArticleSectionEditor } from "./articleSectionEditor";
import { TitleEditor } from "./titleEditor";

type ArticleMainEditorProps = React.HTMLAttributes<HTMLDivElement>;
export const ArticleMainEditor: React.FC<ArticleMainEditorProps> = ({ ...htmlProps }) => {
	const controller = useEditorController();

	const article = useObservable(controller.article);

	const sortBlocks = (result: DropResult): void => {
		const { source, destination } = result;
		if (!destination) {
			return;
		}
		if (source.droppableId === destination.droppableId && source.index === destination.index) {
			return;
		}
		controller.moveSection(source.droppableId, destination.droppableId, source.index, destination.index);
	};

	return (
		<Container {...htmlProps}>
			<TitleEditor />
			<DragDropContext onDragEnd={sortBlocks}>
				<MiddleContainer>
					{article.content.sections.map((section, index) => (
						<Droppable droppableId={section.id} key={section.id}>
							{(droppableProvided) => (
								<>
									<ArticleSectionEditor
										{...droppableProvided.droppableProps}
										droppableRef={droppableProvided.innerRef}
										section={section}
										sectionPlaceholder={droppableProvided.placeholder}
										index={index}
									/>
								</>
							)}
						</Droppable>
					))}
					<AddSectionMenu
						currentlyEditing={false}
						asFirst={
							article.content.sections && isArray(article.content.sections) && article.content.sections.length > 0
						}
					/>
				</MiddleContainer>
			</DragDropContext>
			<RequiredData>{I18n.t("article.editor.required_data")}</RequiredData>
		</Container>
	);
};

const MiddleContainer = styled(RoundedBlock)`
	position: relative;
	padding: 36px 50px 50px;
	${gap(50)}
	@media (max-width: 900px) {
		padding: 20px 25px 20px 20px;
		${gap(20)};
	}
`;

const Container = styled.div`
	${stack(40, "flex-start", "stretch")};
	@media ${mobile} {
		${stack(20, "flex-start", "stretch")};
	}
`;

const RequiredData = styled.div`
	${({ theme }) => theme.textStyles.tertiary};
	margin-top: 15px;
`;
